import React, { useCallback, useMemo, useState } from 'react';
import { Button, Form, InputNumber, Modal, message } from 'antd';
import { getEntityById, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { useForm } from 'antd/lib/form/Form';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { releaseRequestedQuantityForSupplier } from '../../thunks/requested_parts_thunks';
import { updatePartsOnWorkOrderWithPartsPerStockLocation } from '../../thunks/parts_equipments_work_orders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';

const FORM_NAME = 'requested-parts-form';

const RequestedPartsQuantityModal = ({
	releaseText,
	requestedPart,
	onCancel,
	successCallback,
	updatePartsWithQuantity,
	releaseRequestedPart,
	match,
}): React.ReactElement => {
	const [form] = useForm();

	const [saving, setSaving] = useState(false);

	const workOrderId = useMemo(() => match.params.id, [match.params.id]);

	const title = useMemo(
		() =>
			`${releaseText ? releaseText : 'Consume'} ${nullSafeGetOrElse(
				'part.name',
				requestedPart,
				'Part'
			)}`,
		[releaseText, requestedPart]
	);

	const consumePart = useCallback(
		(quantity) => {
			setSaving(true);
			updatePartsWithQuantity({
				workOrderId,
				stockLocationId: nullSafeGet('partsPerStockLocation.stockLocationId', requestedPart),
				partsPerStockLocationId: nullSafeGet('partsPerStockLocation.id', requestedPart),
				partId: nullSafeGet('partId', requestedPart),
				quantity: quantity,
				...(nullSafeGet('id', requestedPart) && {
					workOrderRequestedPartId: nullSafeGet('id', requestedPart),
				}),
			})
				.then(() => successCallback && successCallback())
				.catch(() => message.error('Unable to consume part at this moment!'))
				.finally(() => setSaving(false));
		},
		[updatePartsWithQuantity, workOrderId, requestedPart, successCallback]
	);

	const releasePart = useCallback(
		(requestedQuantity) => {
			setSaving(true);
			releaseRequestedPart(requestedPart, {
				requestedQuantity,
			})
				.then(() => successCallback && successCallback())
				.catch(() => message.error(`Unable to ${releaseText} part at this moment!`))
				.finally(() => setSaving(false));
		},
		[releaseRequestedPart, releaseText, requestedPart, successCallback]
	);

	const isRelease = useMemo(() => !!releaseText, [releaseText]);

	const onSubmit = useCallback(
		(values) => (isRelease ? releasePart(values.quantity) : consumePart(values.quantity)),
		[consumePart, isRelease, releasePart]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title={title}
			closable
			onCancel={onCancel}
			forceRender
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_NAME}
					loading={saving}
				>
					{releaseText ? releaseText : 'Consume'}
				</Button>,
			]}
		>
			<Form form={form} id={FORM_NAME} layout="vertical" requiredMark={false} onFinish={onSubmit}>
				<Form.Item
					name="quantity"
					label="Quantity"
					initialValue={nullSafeGet('requestedQuantity', requestedPart)}
					rules={[
						{
							type: 'number',
							min: 1,
							max: nullSafeGet('requestedQuantity', requestedPart),
							message: `You do not have enough quantity to add`,
						},
						{
							required: true,
						},
					]}
				>
					<InputNumber
						style={{ width: '100%' }}
						min="1"
						disabled={!requestedPart.partsPerStockLocationId}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
});

const mapDispatchToProps = (dispatch) => ({
	updatePartsWithQuantity: (entity) =>
		dispatch(updatePartsOnWorkOrderWithPartsPerStockLocation(ROLE_TYPES.SUPPLIER)(entity)),
	releaseRequestedPart: (entity, quantity) =>
		dispatch(releaseRequestedQuantityForSupplier(entity, quantity)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(RequestedPartsQuantityModal)
);
